import type { RichContent } from '@wix/ricos';
import { PAGE_FORMAT_TO_MM, type PageFormatName, type Theme } from '@wix/ricos/internal';
import { ViewerHeaderBar } from '@wix/wixel-viewer-ui';
import React from 'react';

import { App } from './app';

type PrintLayoutAppProps = {
  content: RichContent;
  design: Theme;
  title: string;
  isPrint: boolean;
  instance?: string;
  pageFormat: string;
  isPagesEnabled: boolean;
};

const getPageCss = (pageFormat: string): string => {
  const key = (pageFormat?.toUpperCase() ?? 'A4') as PageFormatName;
  const dims = PAGE_FORMAT_TO_MM[key] ?? PAGE_FORMAT_TO_MM.A4;

  if (!dims.heightMm) {
    return '';
  }

  return `@page { size: ${dims.widthMm}mm ${dims.heightMm}mm; margin: 0; }`;
};

// The 100vh floors in App.css are on-screen affordances (fill the browser window).
// The PDF capturer derives the page height from the rendered layout — Gotenberg's
// singlePage mode measures the *screen* layout, and for fluid formats the client
// injects an exact @page size measured from body.scrollHeight (see entry-client) —
// so in print mode no height floor may apply in any media.
const PRINT_HEIGHT_CSS =
  'html, body { height: auto; } #root, .editorContainer { min-height: auto; }';

export const PrintLayoutApp: React.FC<PrintLayoutAppProps> = ({
  content,
  design,
  title,
  isPrint,
  instance,
  pageFormat,
  isPagesEnabled,
}) => {
  if (isPrint) {
    const pageCss = getPageCss(pageFormat);

    return (
      <>
        <style>{PRINT_HEIGHT_CSS}</style>
        {pageCss && isPagesEnabled && <style>{pageCss}</style>}
        <div style={{ width: '900px', margin: '0 auto' }}>
          <App
            content={content}
            design={design}
            isPrint={isPrint}
            instance={instance}
            pageFormat={pageFormat}
            isPagesEnabled={isPagesEnabled}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <ViewerHeaderBar name={title} sticky />
      <App
        content={content}
        design={design}
        isPrint={isPrint}
        instance={instance}
        pageFormat={pageFormat}
        isPagesEnabled={isPagesEnabled}
      />
    </>
  );
};
